import { useEffect } from 'react';

export function useGoogleAnalytics() {
  // Use window.location instead of React Router hooks to avoid context issues
  const location = { 
    pathname: window.location.pathname, 
    search: window.location.search 
  };

  useEffect(() => {
    // Skip initialization in development environments
    if (import.meta.env.MODE === 'development') {
      console.log('Google Analytics disabled in development mode');
      return;
    }

    // Initialize gtag.js if not already done
    if (!window.dataLayer) {
      // Load gtag.js script dynamically
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GA_MEASUREMENT_ID}`;
      script.async = true;
      script.onload = () => {
        console.log('Google Analytics script loaded successfully');
      };
      script.onerror = () => {
        console.error('Failed to load Google Analytics script');
      };
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag() {
        dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
        send_page_view: false, // We'll handle page views manually
        cookie_flags: 'SameSite=Strict;Secure'
      });
    }

    // Send page_view event on initial load
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        send_to: import.meta.env.VITE_GA_MEASUREMENT_ID
      });
    }

    // Listen for navigation changes using popstate and hashchange
    const handleLocationChange = () => {
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: document.title,
          page_location: window.location.href,
          page_path: window.location.pathname + window.location.search,
          send_to: import.meta.env.VITE_GA_MEASUREMENT_ID
        });
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []); // Empty dependency array since we're using window.location

  // Helper function to track custom events
  const trackEvent = (eventName, parameters = {}) => {
    if (import.meta.env.MODE === 'production' && typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, {
        ...parameters,
        send_to: import.meta.env.VITE_GA_MEASUREMENT_ID
      });
    }
  };

  return { trackEvent };
}

export default useGoogleAnalytics;